import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Image from 'next/legacy/image';
import { FlattenInterpolation, ThemeProps } from 'styled-components';
import { Col, Popover, Row } from 'antd';
import { CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

import PhotoCarousel, { PhotoCarouselProps } from '@design-system/components/BrowsingCard/PhotoCarousel';
import useMobileView from '@design-system/hooks/useMobileView';

import {
  CountryCode,
  formatCarInfo,
  mappingCountryCurrency,
  countryCurrency,
} from '@design-system/components/BrowsingCard/utils';
import useRegionConfig from '@source/hooks/useRegionConfig';
import get from 'lodash/get';
import { getNewCarCardConfigs } from '@source/pages/BuyNew/constants';
import DisclaimerIcon from '@source/pages/BuyNew/assets/icon-disclaimer.svg';
import InfoIcon from '@source/pages/BuyNew/assets/icon-info.svg';
import {
  StyledDisclaimer,
  StyledModal,
  StyledPriceContent,
  OverridePopoverStyle,
  StyledInstalmentValue,
  StyledPrimaryValue,
  StyledIncludeNote,
  StyledDivider,
  StyledBrandNewBage,
  StyledCardInfo,
  StyledWrapper,
  StyledPhotoWrapper,
  StyledCardContentWrapper,
} from './NewCarCard.style';
import { DiscountRibbonWrapper } from './DiscountRibbon';

export interface NewCardProps extends PhotoCarouselProps {
  car: any;
  region: string;
  href: string;
  wrapperStyles?: FlattenInterpolation<ThemeProps<any>>;
  isExternal?: boolean;
  onOtherOnClickAction?: (params: any) => void;
  language: string;
}

const NewCarCard: FC<NewCardProps> = ({
  isImagePreload,
  car,
  onPhotoClick,
  onPhotoChange,
  region,
  href,
  wrapperStyles,
  hasLazyLoadImageEffect,
  photoSwiperOptions,
  isExternal = false,
  onOtherOnClickAction,
  language = 'en',
}) => {
  const styledWrapperRef = useRef<HTMLAnchorElement>(null);
  const { t } = useTranslation();
  const isMobile = useMobileView(576);
  const [disclaimerModalVisible, setDisclaimerModalVisible] = useState(false);
  const [priceModalVisible, setPriceModalVisible] = useState(false);

  const {
    countryConfig: { country: countryCode },
  } = useRegionConfig();

  const currency = mappingCountryCurrency[region as CountryCode];
  const carData = formatCarInfo(car);
  const configs = car && (getNewCarCardConfigs[region || 'sg'] || getNewCarCardConfigs.sg)(car, currency, isMobile);
  const carPhotos = carData?.photos;
  const carPlaceholderImg = 'carro-webs/assets/shared/icons/car-missing-image.png';

  const disclaimerContent = (
    <StyledDisclaimer role="dialog" aria-labelledby="disclaimer-title">
      <h3 id="disclaimer-title" className="disclaimer-title">
        {t(configs.disclaimer.title)}
      </h3>
      <div className="disclaimer-content">{t(configs.disclaimer.content)}</div>
    </StyledDisclaimer>
  );

  const priceContent = (
    <StyledPriceContent role="dialog" aria-labelledby="price-content-title">
      <h3 id="price-content-title" className="desc">
        {t(configs.priceContent.desc)}
      </h3>
      <ol className="content">
        {configs.priceContent.mainContent.map((item: any, index: number) => (
          <li className="detail-item" key={index as number}>
            <span className="detail">{item}</span>
          </li>
        ))}
      </ol>
      <div className="desc-bottom">{t(configs.priceContent.descBottom)}</div>
    </StyledPriceContent>
  );

  const renderPriceInfo = () =>
    !isMobile ? (
      <Popover overlayClassName="price-popover" content={priceContent} placement="bottom">
        <InfoCircleOutlined style={{ fontSize: '14px', color: '#878787' }} />
      </Popover>
    ) : (
      <div
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        aria-hidden="true"
      >
        <InfoCircleOutlined style={{ fontSize: '14px', color: '#878787' }} onClick={() => setPriceModalVisible(true)} />
        <StyledModal
          visible={priceModalVisible}
          maskClosable
          onCancel={() => setPriceModalVisible(false)}
          footer={null}
          width={304}
          centered
          closeIcon={<CloseCircleOutlined />}
          aria-modal="true"
          aria-labelledby="price-content-title"
        >
          {priceContent}
        </StyledModal>
      </div>
    );

  const renderDisclaimerInfo = () =>
    !isMobile ? (
      <Popover overlayClassName="new-disclaimer-popover" content={disclaimerContent} placement="bottom">
        <Image width="11" height="14" src={DisclaimerIcon} alt="View disclaimer" role="button" />
      </Popover>
    ) : (
      <div
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        aria-hidden="true"
      >
        <Image
          width="11"
          height="14"
          src={DisclaimerIcon}
          onClick={() => setDisclaimerModalVisible(true)}
          alt="View disclaimer"
        />
        <StyledModal
          visible={disclaimerModalVisible}
          maskClosable
          onCancel={() => setDisclaimerModalVisible(false)}
          footer={null}
          width={370}
          centered
          closeIcon={<CloseCircleOutlined />}
          aria-modal="true"
          aria-labelledby="disclaimer-title"
        >
          {disclaimerContent}
        </StyledModal>
      </div>
    );

  const renderPriceInstalment = () => (
    <div className="browsing-card-secondary-details">
      {configs.secondaryValue && (
        <Trans
          defaults={t(`buyNew.listingCard.instalment`)}
          components={{
            styled: <StyledInstalmentValue />,
          }}
          values={{ currency, instalment: configs.secondaryValue }}
        />
      )}
      {configs.disclaimer && <div className="popover-icon">{renderDisclaimerInfo()}</div>}
      {configs.priceContent && <div className="popover-icon">{renderPriceInfo()}</div>}
    </div>
  );

  const renderOriginalPrice = () => {
    if (configs.discountValue.value <= 0) {
      return null;
    }

    const { executivePrice, premiumPrice, executivePriceDisplay, premiumPriceDisplay } = configs.originalValue || {};

    if (!executivePrice && !premiumPrice) {
      return null;
    }

    return (
      <span className="browsing-card-original-price">
        <Trans
          defaults={t(`buyNew.listingCard.originalPrice.${countryCode}`)}
          values={{ currency, value: executivePriceDisplay }}
        />{' '}
        -{' '}
        <Trans
          defaults={t(`buyNew.listingCard.originalPrice.${countryCode}`)}
          values={{ currency, value: premiumPriceDisplay }}
        />
      </span>
    );
  };

  const renderCarDetails = (
    <article>
      <div className="browsing-card-price-details">
        <div className="browsing-card-price">
          <Trans
            defaults={t(`buyNew.listingCard.priceRange.${countryCode}`)}
            components={{
              span: <span className="browsing-card-currency" />,
              styled: <StyledPrimaryValue />,
            }}
            values={{
              currency,
              priceRange: configs.primaryValue.priceRange,
            }}
          />
        </div>

        <div className="browsing-card-include">
          {renderOriginalPrice()} <Trans defaults={t(`buyNew.listingCard.include`)} />
          {renderPriceInstalment()}
        </div>
      </div>

      <StyledDivider />

      <div className="bages">
        <StyledBrandNewBage>{t('buyNew.listingCard.brandNew')}</StyledBrandNewBage>
      </div>

      <h2 className="browsing-card-title">{carData?.title}</h2>

      <StyledCardInfo isMSSG={language === 'ms-SG'}>
        {configs?.carInfo &&
          configs.carInfo[configs.fuelType]?.map((info: any) => {
            const { value, valueInfo, valueMeasure, icon, label } = info || {};
            return (
              <div key={value} className="spec-item">
                <div className="spec-item-label">
                  <Image width="12" height="12" src={icon} alt={t(label)} />
                  {t(label)}:
                </div>
                {car?.inventory?.fuel_type !== 'electric' ? (
                  <div className="spec-item-value">
                    {valueInfo && t(valueInfo)} {get(car, value)} {valueMeasure}
                  </div>
                ) : (
                  <div className="spec-item-value">-</div>
                )}
              </div>
            );
          })}
      </StyledCardInfo>
    </article>
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: carData?.title,
    description: carData?.title,
    image: carData?.photos?.length ? carData?.photos : [carPlaceholderImg],
    url: typeof window !== 'undefined' ? `${window.location.origin}${href}` : undefined,
    brand: {
      '@type': 'Brand',
      name: car?.inventory?.make,
    },
    model: car?.inventory?.model,
    vehicleModelDate: carData?.manufactureYear,
    vehicleTransmission: carData?.transmission,
    fuelType: carData?.fuelType,
    color: carData?.color,
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value: carData?.mileage,
      unitText: 'KM',
    },
    numberOfPreviousOwners: carData?.numberOfOwners,
    offers: {
      '@type': 'Offer',
      priceCurrency: countryCurrency[countryCode as CountryCode] || 'SGD',
      price: carData?.price,
      url: typeof window !== 'undefined' ? `${window.location.origin}${href}` : undefined,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: countryCode?.toUpperCase(),
        },
      },
    },
  };

  return (
    <StyledWrapper
      ref={styledWrapperRef}
      href={href}
      wrapperStyles={wrapperStyles}
      target={isExternal ? '_blank' : '_self'}
      role="article"
      aria-label={`${carData?.title} - New Car Listing`}
    >
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd, null, 2) }} />
      <StyledPhotoWrapper>
        <figure className="photo-carousel">
          <PhotoCarousel
            id={carData.id}
            isImagePreload={isImagePreload}
            onPhotoChange={onPhotoChange}
            isExternal={isExternal}
            onOtherOnClickAction={onOtherOnClickAction}
            onPhotoClick={onPhotoClick}
            photos={carPhotos}
            carPlaceholderImg={carPlaceholderImg}
            alt={carData?.title}
            hasLazyLoadImageEffect={hasLazyLoadImageEffect}
            photoSwiperOptions={photoSwiperOptions}
          />
          {configs.discountValue.value > 0 && (
            <DiscountRibbonWrapper
              discount={configs.discountValue.valueDisplay}
              countryCode={countryCode}
              currency={currency}
            />
          )}
        </figure>
      </StyledPhotoWrapper>
      <StyledCardContentWrapper region={region}>{renderCarDetails}</StyledCardContentWrapper>
      <OverridePopoverStyle />
    </StyledWrapper>
  );
};

NewCarCard.defaultProps = {
  isExternal: false,
  onOtherOnClickAction: undefined,
};

export default NewCarCard;
